import { Alert, Center, Loader } from '@mantine/core';
import type { ReactNode } from 'react';
import { useSubscriptionStatus } from '../lib/hooks/useSubscriptionStatus';
import { UpsellCard } from './UpsellCard';

/**
 * Wraps any write-side UI surface with the shop's subscription gate.
 * Renders children when the shop is subscribed and writes are allowed;
 * renders {@link UpsellCard} when the gate is lapsed; renders a loader while
 * the status query is in-flight (no flicker of the gated content); renders
 * an error alert if the lookup fails outright.
 *
 * Shell: (desktop). Mandatory wrapper for every staff-facing screen that
 * performs writes (see CLAUDE.md "Every product-touching write surface uses
 * SubscriptionGate" — added with Epic 8).
 *
 * Non-obvious contracts:
 *   - Returns null during the loading phase rather than showing a spinner
 *     while ALSO mounting children, because mounting gated children would
 *     fire their data hooks against a shop that may turn out to be lapsed.
 *   - `null` shopId is rendered as the "no shop selected" alert — same as a
 *     shop the user is not a member of (RLS returns null either way).
 */
export interface SubscriptionGateProps {
  shopId: string | null;
  children: ReactNode;
}

export function SubscriptionGate({ shopId, children }: SubscriptionGateProps) {
  const { data, isLoading, isError, error } = useSubscriptionStatus(shopId);

  if (!shopId) {
    return (
      <Alert color="yellow" title="No shop selected">
        Sign in to a shop to manage its inventory.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    );
  }

  if (isError) {
    return (
      <Alert color="red" title="Could not load subscription status">
        {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert color="yellow" title="Shop not found">
        This shop is not visible to your account. If you believe this is a mistake, contact your
        owner or <a href="mailto:support@mytcgbinder.com">support</a>.
      </Alert>
    );
  }

  if (!data.writesAllowed) {
    return <UpsellCard tierId={data.tierId} status={data.status} />;
  }

  return <>{children}</>;
}
